// The number the served design shows on its header cart badge, read from the cart the design itself persists (`sg-cart-v1`, SG-CART-PERSIST-021/023).
//
// Pure. The design's badge is `cartCount = cart.reduce((a, l) => a + l.qty, 0)` over its cart lines. The design restores a line only when it is an object
// with a string `id`, a finite `qty > 0` and a finite `price >= 0` (see `SG-CART-PERSIST-021` in the compiled design), so the same filter is applied here:
// a line the design would not restore is not counted. The result is `null` (UNKNOWN) when there is no readable v1 record: a missing, unparseable or
// foreign record is never zero. Zero is only ever an explicit record whose cart holds no restorable line (the design persisted an empty cart).
//
// Display metadata only: nothing here prices, sums money or reaches checkout.

export const DESIGN_CART_KEY = "sg-cart-v1";
/** The largest count the server snapshot holds (its integer column). A larger sum is not truncated: it is unknown and nothing is published. */
export const CART_BADGE_MAX = 2147483647;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

export function designCartBadge(raw: string | null | undefined): number | null {
  if (typeof raw !== "string" || raw === "") return null;
  let saved: unknown;
  try {
    saved = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObj(saved) || saved.v !== 1 || !Array.isArray(saved.cart)) return null;
  let sum = 0;
  for (const line of saved.cart) {
    if (!isObj(line) || typeof line.id !== "string") continue;
    const qty = Number(line.qty);
    const price = Number(line.price);
    if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price) || price < 0) continue;
    sum += qty;
  }
  // a non-integer or out-of-range total is not a count the badge can honestly show: unknown, never rounded or clamped
  return Number.isSafeInteger(sum) && sum <= CART_BADGE_MAX ? sum : null;
}
